/**
 * PeriodTargets - Weekly/Monthly fundraising targets management
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Target, Calendar, TrendingUp, Loader2, Trash2, Edit,
  CheckCircle2, Clock, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PeriodTargets = ({ campaignId }) => {
  const queryClient = useQueryClient();
  const [showAddTarget, setShowAddTarget] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  
  const [targetForm, setTargetForm] = useState({
    period_type: 'weekly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    target_amount: '',
    label: ''
  });

  // Get token helper
  const getToken = () => {
    try {
      const authStorage = localStorage.getItem('eventflow-auth');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        return parsed?.state?.token || localStorage.getItem('eventflow_token');
      }
    } catch (e) {}
    return localStorage.getItem('eventflow_token');
  };

  // Fetch period targets
  const { data: targetsData, isLoading } = useQuery({
    queryKey: ['period-targets', campaignId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${campaignId}/period-targets`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error('Failed to fetch targets');
      return res.json();
    },
    enabled: !!campaignId
  });

  // Create target mutation
  const createTargetMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/campaigns/${campaignId}/period-targets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create target');
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success('Target created successfully!');
      queryClient.invalidateQueries(['period-targets', campaignId]);
      setShowAddTarget(false);
      resetForm();
    },
    onError: (err) => toast.error(err.message)
  });

  // Update target mutation
  const updateTargetMutation = useMutation({
    mutationFn: async ({ targetId, data }) => {
      const res = await fetch(`${API_URL}/api/campaigns/${campaignId}/period-targets/${targetId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update target');
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success('Target updated!');
      queryClient.invalidateQueries(['period-targets', campaignId]);
      setEditingTarget(null);
      resetForm();
    },
    onError: (err) => toast.error(err.message)
  });

  // Delete target mutation
  const deleteTargetMutation = useMutation({
    mutationFn: async (targetId) => {
      const res = await fetch(`${API_URL}/api/campaigns/${campaignId}/period-targets/${targetId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error('Failed to delete target');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Target deleted');
      queryClient.invalidateQueries(['period-targets', campaignId]);
    },
    onError: (err) => toast.error(err.message)
  });

  const resetForm = () => {
    setTargetForm({
      period_type: 'weekly',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      target_amount: '',
      label: ''
    });
  };

  const handleSubmit = () => {
    if (!targetForm.target_amount || parseFloat(targetForm.target_amount) <= 0) {
      toast.error('Please enter a valid target amount');
      return;
    }
    if (!targetForm.start_date) {
      toast.error('Start date is required');
      return;
    }

    const data = {
      ...targetForm,
      target_amount: parseFloat(targetForm.target_amount),
      label: targetForm.label || `${targetForm.period_type.charAt(0).toUpperCase() + targetForm.period_type.slice(1)} Target`
    };

    if (editingTarget) {
      updateTargetMutation.mutate({ targetId: editingTarget.id, data });
    } else {
      createTargetMutation.mutate(data);
    }
  };

  const openEditDialog = (target) => {
    setEditingTarget(target);
    setTargetForm({
      period_type: target.period_type,
      start_date: target.start_date,
      end_date: target.end_date || '',
      target_amount: target.target_amount.toString(),
      label: target.label
    });
    setShowAddTarget(true);
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 75) return 'bg-blue-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusIcon = (percent) => {
    if (percent >= 100) return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    if (percent >= 50) return <TrendingUp className="h-5 w-5 text-blue-500" />;
    return <AlertCircle className="h-5 w-5 text-orange-500" />;
  };

  const targets = targetsData?.targets || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Period Targets
              </CardTitle>
              <CardDescription>Set weekly or monthly fundraising goals</CardDescription>
            </div>
            <Dialog 
              open={showAddTarget} 
              onOpenChange={(open) => {
                setShowAddTarget(open);
                if (!open) {
                  setEditingTarget(null);
                  resetForm();
                }
              }}
            >
              <DialogTrigger asChild>
                <Button data-testid="add-target-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Target
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingTarget ? 'Edit Target' : 'Add Period Target'}</DialogTitle>
                  <DialogDescription>
                    Set a weekly or monthly fundraising goal
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Target Label</Label>
                    <Input
                      value={targetForm.label}
                      onChange={(e) => setTargetForm({ ...targetForm, label: e.target.value })}
                      placeholder="e.g., Week 1 Target, April Goal"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Period Type</Label>
                      <Select
                        value={targetForm.period_type}
                        onValueChange={(value) => setTargetForm({ ...targetForm, period_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Target Amount *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={targetForm.target_amount}
                        onChange={(e) => setTargetForm({ ...targetForm, target_amount: e.target.value })}
                        placeholder="5000"
                        data-testid="target-amount-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date *</Label>
                      <Input
                        type="date"
                        value={targetForm.start_date}
                        onChange={(e) => setTargetForm({ ...targetForm, start_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={targetForm.end_date}
                        onChange={(e) => setTargetForm({ ...targetForm, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddTarget(false)}>Cancel</Button>
                  <Button 
                    onClick={handleSubmit}
                    disabled={createTargetMutation.isPending || updateTargetMutation.isPending}
                    data-testid="submit-target-btn"
                  >
                    {(createTargetMutation.isPending || updateTargetMutation.isPending) && (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    )}
                    {editingTarget ? 'Update Target' : 'Create Target'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {targets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No period targets set</p>
              <p className="text-sm">Add weekly or monthly targets to track progress</p>
            </div>
          ) : (
            <div className="space-y-4">
              {targets.map((target) => (
                <div key={target.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(target.progress_percent)}
                      <div>
                        <h4 className="font-semibold">{target.label}</h4>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Badge variant="outline">{target.period_type}</Badge>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {target.start_date}
                            {target.end_date && ` - ${target.end_date}`}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(target)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => {
                          if (window.confirm('Delete this target?')) {
                            deleteTargetMutation.mutate(target.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">
                        ${target.current_amount?.toLocaleString() || 0} raised
                      </span>
                      <span className="text-muted-foreground">
                        of ${target.target_amount?.toLocaleString()} goal
                      </span>
                    </div>
                    <div className="relative">
                      <Progress 
                        value={Math.min(target.progress_percent || 0, 100)} 
                        className="h-3"
                      />
                      {target.progress_percent >= 100 && (
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 -mr-6">
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{target.progress_percent || 0}% complete</span>
                      {target.progress_percent < 100 && (
                        <span>
                          ${((target.target_amount || 0) - (target.current_amount || 0)).toLocaleString()} to go
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PeriodTargets;
